import DeviceControls from 'components/DeviceControls/DeviceControls';
import DeviceHistory from 'components/DeviceHistory/DeviceHistory';
import FusionDropDownInput from 'components/FusionDropDownInput/FusionDropDownInput';
import GroupSettingsDeviceList from 'components/GroupSettingsDeviceList/GroupSettingsDeviceList';
import DeviceMap from 'features/integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { useEffect, useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { ErrorMessage } from './ErrorMessage';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import WidgetTimeUpdate from './WidgetTimeUpdate';

export default function DeviceGroupWidget({ dimensions }) {
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState({ state: false, message: '' });
  const ROUTE = 'deviceGroupList';
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroupIndex, setSelectedGroupIndex] = useState(0);

  const [virtualDevice, setVirtualDevice] = useState();
  const [sending, setSending] = useState(false);
  const [displayMessage, setDisplayMessage] = useState();
  const [audioOverride, setAudioOverride] = useState(null);
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  const groupDevices = useMemo(() => {
    if (!selectedGroup?.devices?.length) return [];
    const idSet = new Set(selectedGroup.devices);
    return connectedDevices.filter((device) => idSet.has(device.id));
  }, [connectedDevices, selectedGroup]);

  const getGroupMapBounds = (devices) => {};

  const deviceMapBounds = useMemo(() => getGroupMapBounds(groupDevices), [groupDevices]);

  const getSirenDeviceIds = () => {
    if (!selectedGroup?.devices?.length) return [];
    return (
      connectedDevices
        ?.filter(
          (_device) =>
            selectedGroup.devices?.includes(_device.id) &&
            _device.device_type === 'Technology Bridge_Siren/Giant Voice',
        )
        .map((_device) => _device.id) || []
    );
  };

  useEffect(() => {
    if (!selectedGroup) return;
    const devices = selectedGroup.devices
      .map((deviceId) => connectedDevices?.find((device) => device.id === deviceId))
      .filter((device) => !!device);

    let device = devices.find((device) => {
      //check if device type is correct
      const type = selectedGroup.type;
      let typeCheck = true;

      if (type === 'Technology Bridge_Flashing Lights')
        typeCheck = ['Technology Bridge_Flashing Lights', 'Manual Road Closure'].includes(
          device.device_type,
        );
      else if (type !== '') typeCheck = device.device_type === type;

      if (selectedGroup.devices?.includes(device.id))
        return selectedGroup.devices?.includes(device.id) && typeCheck;
    });

    const data = connectedDevices
      ?.filter(
        (_device) =>
          selectedGroup.devices?.includes(_device.id) &&
          _device.device_type.toLowerCase() !== 'stage',
      )
      .map((_device) => _device?.data)
      .flatMap((v) => v)
      .sort((a, b) => b.ts - a.ts);

    setVirtualDevice({
      ...device,
      data: data,
      device_type: selectedGroup?.type || device?.device_type || '',
    });
  }, [selectedGroup, connectedDevices]);

  const deviceControlListener = async (evt, data) => {
    switch (evt) {
      case 'update':
        setVirtualDevice(data);
        return;
      case 'submit':
        if (!selectedGroup?.id) return;
        setSending(true);
        try {
          const statusValue = Number(data?.status ?? 0);
          const commentsValue = data?.comments ?? '';
          const commandArgs = {};
          const intervalValue = Number(data?.interval);
          if (Number.isFinite(intervalValue)) commandArgs.interval = intervalValue;
          const repeatValue = Number(data?.repeat);
          if (Number.isFinite(repeatValue)) commandArgs.repeat = repeatValue;
          const volumeValue = Number(data?.volume);
          if (Number.isFinite(volumeValue)) commandArgs.volume = volumeValue;

          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments,
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments,
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
        } catch (error) {
          console.error(error.message);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        if (!selectedGroup?.id) return;
        try {
          sendDeviceCommand(
            {
              id: virtualDevice.id,
              node_id: virtualDevice.node_id,
              system_type: virtualDevice.system_type,
              device_type: virtualDevice.device_type,
            },
            7,
            '',
            false,
          );
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setDisplayMessage({
            type: 'error',
            message: 'Failed to send command!',
          });
        }
        return;
      case 'audio-manager':
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    if (deviceGroupsLoading) return; // Wait for data to load before syncing state
    if (!deviceGroups) {
      console.error('DeviceGroupWidget: deviceDataHook is not available');
      return;
    }

    setGroups(deviceGroups);
    setSelectedGroup(deviceGroups[0] || null);
  }, [deviceGroups, deviceGroupsLoading]);

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  if (fetching)
    return <WidgetLoadingAnimation widgetName={'MyAdvisories'} dimensions={dimensions} />;

  if ((dimensions?.[1] || 0) >= 2) {
    const mapHeight = 180;
    const historyMaxHeight = 220;
    return (
      <div className="h-100">
        <WidgetTimeUpdate route={ROUTE} realtime />
        {connectedDevices && connectedDevices.length > 0 && (
          <div
            className="d-flex flex-column overflow-x-hidden px-1 gap-2"
            style={{ height: 'calc(100% - 20px)' }}
          >
            <div className="w-100 overflow-hidden rounded-2" style={{ height: mapHeight }}>
              <DeviceMap
                mode="group"
                devicesOverride={groupDevices}
                mapBoundsOverride={deviceMapBounds}
                filtersCompact
              />
            </div>

            <div className="row align-items-center g-2">
              <div className="col-4 text-nowrap">Group Alias:</div>
              <div className="col-8">
                <FusionDropDownInput
                  onChange={(e) => {
                    if (!e || e === '') {
                      setSelectedGroup(null);
                      setSelectedGroupIndex(0);
                      return;
                    }
                    setSelectedGroup(groups[parseInt(e)]);
                    setSelectedGroupIndex(parseInt(e));
                  }}
                  options={groups.map((group) => group.name)}
                  value={selectedGroupIndex}
                />
              </div>
            </div>

            {selectedGroup?.devices?.length > 0 && selectedGroup?.devices[0] ? (
              <DeviceControls
                device={virtualDevice}
                devices={(connectedDevices || []).filter((device) =>
                  getSirenDeviceIds().includes(device.id),
                )}
                displayMessage={displayMessage}
                on={deviceControlListener}
                sending={sending}
                audioOverride={audioOverride}
                audioManagerButtonEnabled={false}
              />
            ) : (
              <div>Please select a valid group.</div>
            )}

            <div style={{ flex: 1, minHeight: 160 }}>
              <GroupSettingsDeviceList
                devices={groupDevices}
                highlightIds={
                  virtualDevice?.audio?.device_ids ? [...virtualDevice.audio.device_ids] : []
                }
              />
            </div>

            {virtualDevice?.data?.length > 0 && (
              <Accordion>
                <Accordion.Item eventKey="0">
                  <Accordion.Header>Group Device History</Accordion.Header>
                  <Accordion.Body className="px-0">
                    <div className="w-100 overflow-auto" style={{ maxHeight: historyMaxHeight }}>
                      <DeviceHistory
                        title="Group Device History"
                        data={virtualDevice.data}
                        deviceType={virtualDevice.device_type}
                        hideHeader
                      />
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            )}
          </div>
        )}
      </div>
    );
  }

  if (dimensions[0] === 2 && dimensions[1] === 1) {
    const mapHeight = 220;
    const historyMaxHeight = 240;
    return (
      <div className="h-100">
        <WidgetTimeUpdate route={ROUTE} realtime />
        <div className="overflow-x-hidden p-1" style={{ height: 'calc(100% - 20px)' }}>
          {connectedDevices && connectedDevices.length > 0 && (
            <div
              className="d-flex flex-column overflow-x-hidden px-1 gap-2"
              style={{ height: 'calc(100% - 20px)' }}
            >
              <div className="d-flex gap-2">
                <div className="flex-fill overflow-hidden rounded-2" style={{ height: mapHeight }}>
                  <DeviceMap
                    mode="group"
                    devicesOverride={groupDevices}
                    mapBoundsOverride={deviceMapBounds}
                    filtersCompact
                  />
                </div>

                <div className="flex-fill" style={{ minWidth: 0 }}>
                  <div className="row align-items-center g-2 mb-2">
                    <div className="col-4 text-nowrap">Group Alias:</div>
                    <div className="col-8">
                      <FusionDropDownInput
                        onChange={(e) => {
                          if (!e || e === '') {
                            setSelectedGroup(null);
                            setSelectedGroupIndex(0);
                            return;
                          }
                          setSelectedGroup(groups[parseInt(e)]);
                          setSelectedGroupIndex(parseInt(e));
                        }}
                        options={groups.map((group) => group.name)}
                        value={selectedGroupIndex}
                      />
                    </div>
                  </div>

                  {selectedGroup?.devices?.length > 0 && selectedGroup?.devices[0] ? (
                    <DeviceControls
                      device={virtualDevice}
                      devices={(connectedDevices || []).filter((device) =>
                        getSirenDeviceIds().includes(device.id),
                      )}
                      displayMessage={displayMessage}
                      on={deviceControlListener}
                      sending={sending}
                      audioOverride={audioOverride}
                      audioManagerButtonEnabled={false}
                    />
                  ) : (
                    <div>Please select a valid group.</div>
                  )}
                </div>
              </div>

              <div className="flex-fill" style={{ minHeight: 160 }}>
                <GroupSettingsDeviceList
                  devices={groupDevices}
                  highlightIds={
                    virtualDevice?.audio?.device_ids ? [...virtualDevice.audio.device_ids] : []
                  }
                />
              </div>

              {virtualDevice?.data?.length > 0 && (
                <Accordion>
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>Group Device History</Accordion.Header>
                    <Accordion.Body className="px-0">
                      <div className="w-100 overflow-auto" style={{ maxHeight: historyMaxHeight }}>
                        <DeviceHistory
                          title="Group Device History"
                          data={virtualDevice.data}
                          deviceType={virtualDevice.device_type}
                          hideHeader
                        />
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}
